import React, { useEffect, useMemo, useState } from "react";
import API from "../../api";
import "../css/listMerchant_id.css"; // reuse same table styling
import CreateTicket from "./CreateTicket";

function safeJsonParse(s) {
    try {
        return JSON.parse(s);
    } catch {
        return null;
    }
}

function normalizeRole(role) {
    return String(role || "")
        .trim()
        .toLowerCase()
        .replace(/_/g, "-")
        .replace(/\s+/g, "-");
}

function normalizeStr(v) {
    return String(v || "").trim().toLowerCase();
}

/** JWT decode (if you store a JWT token). */
function getUserFromToken() {
    const tok = window.localStorage.getItem("token") || window.sessionStorage.getItem("token");
    if (!tok) return {};
    const parts = tok.split(".");
    if (parts.length < 2) return {};
    try {
        const json = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
        return json?.user || json || {};
    } catch {
        return {};
    }
}

/** Read persisted auth from redux-persist (same approach as your Drafts file). */
function getPersistedAuth() {
    const keysToTry = ["persist:root", "persistroot"];
    for (const key of keysToTry) {
        const raw = window.localStorage.getItem(key);
        if (!raw) continue;

        const root = safeJsonParse(raw);
        if (!root) continue;

        const authObj = typeof root?.auth === "string" ? safeJsonParse(root.auth) : root?.auth;
        if (authObj) return authObj;
    }
    return null;
}

/** Prefer redux-persist auth, then token, then localStorage user object, then localStorage role. */
function getCurrentAuth() {
    const persisted = getPersistedAuth();
    if (persisted) {
        const pUser = persisted?.user || persisted;
        const username = pUser?.username || persisted?.username || "";
        const role = pUser?.role || persisted?.role || "";
        return { username: String(username || ""), role: String(role || "") };
    }

    const tUser = getUserFromToken();
    const tUsername = tUser?.username || tUser?.preferred_username || "";
    const tRole = tUser?.role || "";
    if (tUsername || tRole) return { username: String(tUsername || ""), role: String(tRole || "") };

    const rawUser = window.localStorage.getItem("user");
    if (rawUser) {
        const uObj = safeJsonParse(rawUser);
        if (uObj) {
            const uUsername = uObj?.username || "";
            const uRole = uObj?.role || "";
            if (uUsername || uRole) return { username: String(uUsername || ""), role: String(uRole || "") };
        }
    }

    const lsRole = window.localStorage.getItem("role") || "";
    return { username: "", role: String(lsRole) };
}

/** Extract list from many possible API shapes */
function extractList(payload) {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;

    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.rows)) return payload.rows;

    if (payload.data && typeof payload.data === "object") {
        if (Array.isArray(payload.data.items)) return payload.data.items;
        if (Array.isArray(payload.data.data)) return payload.data.data;
        if (payload.data.data && typeof payload.data.data === "object" && Array.isArray(payload.data.data.items)) {
            return payload.data.data.items;
        }
        if (Array.isArray(payload.data.results)) return payload.data.results;
    }

    for (const v of Object.values(payload)) {
        if (Array.isArray(v)) return v;
        if (v && typeof v === "object") {
            for (const vv of Object.values(v)) {
                if (Array.isArray(vv)) return vv;
            }
        }
    }
    return [];
}

/** Normalize role for API */
function roleForApi(role) {
    const r = normalizeRole(role);
    if (r === "subadmin") return "sub-admin";
    return r;
}

function formatDateTime(value) {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    try {
        return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    } catch {
        return null;
    }
}

/** status -> class (reuse same logic style) */
function statusClass(status) {
    const raw = String(status || "").trim();
    const s = raw.toLowerCase().replace(/\s+/g, "");

    if (!s) return "cb-status-neutral";
    if (raw === "HF-Filled") return "cb-status-draft";
    if (s.includes("declined") || s.includes("notinterested")) return "cb-status-notintrested";
    if (s.includes("sent") || s.includes("submitted")) return "cb-status-callback";
    if (s.includes("signed")) return "cb-status-sold";
    if (s.includes("approved")) return "cb-status-answering";
    if (s.includes("pendingapproval")) return "cb-status-neutral";

    return "cb-status-neutral";
}

export default function ListMerchat_id() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const [currentRole, setCurrentRole] = useState("");
    const [currentUsername, setCurrentUsername] = useState("");

    const [ticketOpen, setTicketOpen] = useState(false);
    const [selectedMPA, setSelectedMPA] = useState(null);

    // ✅ Only filter: Search
    const [searchTerm, setSearchTerm] = useState("");

    const roleNorm = useMemo(() => normalizeRole(currentRole), [currentRole]);
    const isAgent = roleNorm === "agent";

    useEffect(() => {
        let ignore = false;

        (async () => {
            try {
                setLoading(true);
                setErr("");

                const auth = getCurrentAuth();
                const role = roleForApi(auth.role);
                const username = auth.username;

                if (!ignore) {
                    setCurrentRole(role);
                    setCurrentUsername(username);
                }

                // If agent, don't even fetch; show not authorized
                if (normalizeRole(role) === "agent") {
                    if (!ignore) setRows([]);
                    return;
                }

                const resp = await API.post("/auth/mpa-merchant-id-list", { role, username });
                const list = extractList(resp?.data);

                // Map rows for rendering
                const mapped = (Array.isArray(list) ? list : []).map((r, idx) => ({
                    ...r, // ✅ keep all fields for modal
                    id: `${r.merchant_id || "no-mid"}-${idx}`,
                    created_by: r.created_by ?? r.createdby ?? "", // normalize
                    status: r.status ?? "Draft",
                }));


                if (!ignore) setRows(mapped);
            } catch (e) {
                console.error(e);

                // If backend returns 403 for agent, or any forbidden
                const status = e?.response?.status;
                if (!ignore && status === 403) {
                    setRows([]);
                    setErr("");
                    return;
                }

                if (!ignore) setErr("Could not load items.");
            } finally {
                if (!ignore) setLoading(false);
            }
        })();

        return () => {
            ignore = true;
        };
    }, []);

    const filteredRows = useMemo(() => {
        const q = normalizeStr(searchTerm);
        if (!q) return rows;

        return rows.filter((r) => {
            const haystack = [r.merchant_id, r.created_by, r.status, r.updated_at]
                .filter((v) => v !== null && v !== undefined)
                .map((v) => normalizeStr(v))
                .join(" | ");

            return haystack.includes(q);
        });
    }, [rows, searchTerm]);

    if (isAgent) {
        return (
            <div className="cb-wrap">
                <div className="cb-header">
                    <div className="cb-header-left">
                        <label className="cb-chip ht-chip">
                            <input type="radio" name="merchant" defaultChecked />
                            <span>Merchant IDs</span>
                        </label>
                    </div>
                </div>

                <div className="cb-table Merchant-table">
                    <div className="cb-row">
                        <div className="cb-col cb-col-full cb-error">You are not authorized</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cb-wrap">
            <div className="cb-header">
                <div className="cb-header-left">
                    <label className="cb-chip ht-chip">
                        <input type="radio" name="merchant" defaultChecked />
                        <span>Merchant IDs</span>
                    </label>

                    {/* ✅ ONLY SEARCH BOX (no other filters) */}
                    <div className="cb-filters">
                        <div className="cb-filter-group">
                            <input
                                type="text"
                                className="cb-filter-select"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                aria-label="Search merchant ids"
                            />
                        </div>

                        {searchTerm ? (
                            <button
                                type="button"
                                className="cb-page-btn"
                                onClick={() => setSearchTerm("")}
                                style={{ marginLeft: 8 }}
                            >
                                Clear
                            </button>
                        ) : null}
                    </div>
                </div>

                {/* ✅ pagination/filter removed as requested */}
                <div className="cb-pagination-bar">
                    <div className="cb-page-size">
                        <span className="cb-page-meta">{filteredRows.length} total</span>
                    </div>
                </div>
            </div>

            <div className="cb-table Merchant-table">
                <div className="cb-row cb-row-head">
                    <div className="cb-col cb-col-name">Merchant ID</div>
                    <div className="cb-col cb-col-name">Business Name</div>
                    <div className="cb-col cb-col-name">DBA Name</div>
                    <div className="cb-col cb-col-createdby">Created By</div>
                    <div className="cb-col cb-col-status">Status</div>
                    <div className="cb-col cb-col-updatedat">Updated At</div>
                    <div className="cb-col cb-col-menu">Action</div>
                </div>

                {loading && (
                    <div className="cb-row">
                        <div className="cb-col cb-col-full">Loading…</div>
                    </div>
                )}

                {err && (
                    <div className="cb-row">
                        <div className="cb-col cb-col-full cb-error">{err}</div>
                    </div>
                )}

                {!loading && !err && filteredRows.length === 0 && (
                    <div className="cb-row">
                        <div className="cb-col cb-col-full">No items.</div>
                    </div>
                )}

                {!loading &&
                    !err &&
                    filteredRows.map((r) => (
                        <div className="cb-row" key={r.id}>
                            <div className="cb-col cb-col-name">{r.merchant_id || <span className="cb-muted">—</span>}</div>
                            <div className="cb-col cb-col-name">{r.bname || <span className="cb-muted">—</span>}</div>
                            <div className="cb-col cb-col-name">{r["DBA-Name"] || <span className="cb-muted">—</span>}</div>

                            <div className="cb-col cb-col-createdby">{r.created_by || <span className="cb-muted">—</span>}</div>

                            <div className="cb-col cb-col-status">
                                {r.status ? (
                                    <span className={`cb-status ${statusClass(r.status)}`}>{r.status}</span>
                                ) : (
                                    <span className="cb-muted">—</span>
                                )}
                            </div>

                            <div className="cb-col cb-col-updatedat">
                                {formatDateTime(r.updated_at) || <span className="cb-muted">—</span>}
                            </div>

                            <div className="cb-col cb-col-menu">
                                {/* Action button placeholder - wire it to whatever you need */}
                                <button
                                    className="cb-kebab"
                                    type="button"
                                    onClick={() => {
                                        setSelectedMPA(r);   // pass row data
                                        setTicketOpen(true);
                                    }}
                                    title="Create Support Ticket"
                                >
                                    Open
                                </button>

                            </div>
                        </div>
                    ))}
            </div>

            {/* Optional: small debug (remove if you don't want) */}
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
                Role: {currentRole || "—"} | User: {currentUsername || "—"}
            </div>


            <CreateTicket
                open={ticketOpen}
                onClose={() => {
                    setTicketOpen(false);
                    setSelectedMPA(null);
                }}
                mpa={selectedMPA}
                onCreated={() => {
                    // optional: toast, refresh list, etc.
                }}
            />

        </div>
    );
}
